import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Stack,
  Tooltip,
  Divider,
} from "@mui/material";

/**
 * Expects data = {
 *   cred_data: string,
 *   sports_data: [
 *     {
 *       internal_ref, betfair_match_date, polymarket_match_date,
 *       title_betfair, title_poly,
 *       back_poly_lay_betfair: {
 *         home: { poly_back_odds, betfair_lay_odds, difference, arbitrage_opportunity },
 *         draw: {...}, away: {...}
 *       }
 *     }
 *   ]
 * }
 */
export default function SportsDeltaTable({ data }) {
  const [onlyArbs, setOnlyArbs] = useState(false);
  const [sortByEdge, setSortByEdge] = useState(true);

  function fmt(n) {
    if (n === null || n === undefined || isNaN(Number(n))) return "—";
    return Number(n).toFixed(3);
  }

  function formatISO(iso) {
    try {
      const d = new Date(iso);
      return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(d);
    } catch (e) {
      return iso;
    }
  }

  function getObj(o, key) {
    return o && o[key] ? o[key] : null;
  }

  function maxEdge(m) {
    const o = m && m.back_poly_lay_betfair ? m.back_poly_lay_betfair : {};
    const h = getObj(o, "home");
    const d = getObj(o, "draw");
    const a = getObj(o, "away");
    const hd = h && typeof h.difference === "number" ? h.difference : -Infinity;
    const dd = d && typeof d.difference === "number" ? d.difference : -Infinity;
    const ad = a && typeof a.difference === "number" ? a.difference : -Infinity;
    return Math.max(hd, dd, ad);
  }

  const rows = useMemo(function () {
    let list = data && data.sports_data ? data.sports_data.slice() : [];

    if (onlyArbs) {
      list = list.filter(function (m) {
        const o = m.back_poly_lay_betfair || {};
        const h = o.home;
        const d = o.draw;
        const a = o.away;
        return (
          (h && h.arbitrage_opportunity) ||
          (d && d.arbitrage_opportunity) ||
          (a && a.arbitrage_opportunity)
        );
      });
    }

    if (sortByEdge) {
      list.sort(function (a, b) {
        return maxEdge(b) - maxEdge(a);
      });
    }

    return list;
  }, [data, onlyArbs, sortByEdge]);

  function OutcomeCell(props) {
    const label = props.label;
    const obj = props.obj;
    if (!obj) return <TableCell align="center">—</TableCell>;
    const arb = !!obj.arbitrage_opportunity;
    return (
      <TableCell
        sx={{
          backgroundColor: arb ? "#eaf7ee" : "#fafafa",
          borderLeft: "1px solid #eee",
          borderRight: "1px solid #eee",
        }}
      >
        <Stack spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
            {label}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Tooltip title="Polymarket back odds">
              <Chip size="small" label={"Poly " + fmt(obj.poly_back_odds)} />
            </Tooltip>
            <Tooltip title="Betfair lay odds">
              <Chip size="small" label={"Lay " + fmt(obj.betfair_lay_odds)} />
            </Tooltip>
            <Tooltip title="Difference (Poly - Lay)">
              <Chip
                size="small"
                label={"Δ " + fmt(obj.difference)}
                color={arb ? "success" : obj.difference < 0 ? "error" : "default"}
                variant={arb ? "filled" : "outlined"}
              />
            </Tooltip>
          </Stack>
          {/*If label is draw I want to show the raw poly market odds */}
          {/*raw_data_type : ["0.29", "0.71"]*/}
          {label === "Draw" && obj.raw_data_type && (
            <Tooltip title="Raw Polymarket odds">
              <Typography variant="caption" color="text.secondary">
                (Raw Polymarket Odds: {obj.raw_data_type.join(" / ")})
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
    );
  }

  return (
    <Box p={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={data && data.cred_data ? data.cred_data : "SSO status unknown"}
            color="info"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            Showing {rows.length} {onlyArbs ? "opportunities" : "matches"}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={onlyArbs}
                onChange={function (e) {
                  setOnlyArbs(e.target.checked);
                }}
              />
            }
            label="Only arbitrage"
          />
          <FormControlLabel
            control={
              <Switch
                checked={sortByEdge}
                onChange={function (e) {
                  setSortByEdge(e.target.checked);
                }}
              />
            }
            label="Sort by edge"
          />
        </Stack>
      </Stack>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Match</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Ref</TableCell>
              <TableCell align="center">Home</TableCell>
              <TableCell align="center">Draw</TableCell>
              <TableCell align="center">Away</TableCell>
              <TableCell align="center">Max Δ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(function (m) {
              const o = m.back_poly_lay_betfair || {};
              const h = o.home || null;
              const d = o.draw || null;
              const a = o.away || null;

              const hd = h && typeof h.difference === "number" ? h.difference : -Infinity;
              const dd = d && typeof d.difference === "number" ? d.difference : -Infinity;
              const ad = a && typeof a.difference === "number" ? a.difference : -Infinity;
              const maxd = Math.max(hd, dd, ad);

              const anyArb =
                (h && h.arbitrage_opportunity) ||
                (d && d.arbitrage_opportunity) ||
                (a && a.arbitrage_opportunity);

              return (
                <TableRow key={m.internal_ref} hover selected={!!anyArb}>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Typography variant="subtitle2">{m.title_betfair}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.title_poly}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption">
                        <strong>BF:</strong> {formatISO(m.betfair_match_date)}
                      </Typography>
                      <Typography variant="caption">
                        <strong>PM:</strong> {formatISO(m.polymarket_match_date)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={m.internal_ref} variant="outlined" />
                  </TableCell>
                  <OutcomeCell label="Home" obj={h} />
                  <OutcomeCell label="Draw" obj={d} />
                  <OutcomeCell label="Away" obj={a} />
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={isFinite(maxd) ? maxd.toFixed(3) : "—"}
                      color={maxd > 0 ? "success" : "default"}
                      variant={maxd > 0 ? "filled" : "outlined"}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary">
        Green cells = arbitrage-positive outcome (Poly back &gt; Betfair lay). Rows with any opportunity are highlighted.
      </Typography>
    </Box>
  );
}
